/**
 * JobTimeInput.js - 업무 투입시간 입력 페이지 (User) - V3 개편
 * 통합 검색 자동완성, 최근 사용 항목, 빠른 입력 모드 지원
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  InputAdornment,
  Tooltip,
  Chip,
  Autocomplete,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import { Plus, Trash2, Save, X, Search, Clock, Zap, Grid, Edit2, RefreshCw, Download, ChevronUp, ChevronDown, Filter, Minus } from 'react-feather';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { useMobileView } from '../contexts/MobileViewContext';
import axios from 'axios';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import ResizableTableRow from '../components/ResizableTableRow';
import MobileBottomSheet from '../components/MobileBottomSheet';
import MobileTodoDrawer from '../components/MobileTodoDrawer';
import ConfirmModal from '../components/ConfirmModal';
import InfoModal from '../components/InfoModal';
import apiConfig from '../config/api';
import { validateData, validateDataWithAllErrors, TimelogCreateSchema, TimelogUpdateSchema, TimelogBatchCreateSchema } from '../utils/validation';

const API_BASE_URL = apiConfig.V1;

const JobTimeInput = () => {
  const { colors, isDark } = useCustomTheme();
  const { isMobileView } = useMobileView();
  
  // 날짜 상태
  const getDefaultFromDate = () => {
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);
    return format(sevenDaysAgo, 'yyyy-MM-dd');
  };
  const getDefaultToDate = () => format(new Date(), 'yyyy-MM-dd');
  
  const [fromDate, setFromDate] = useState(getDefaultFromDate()); // 7일 전
  const [toDate, setToDate] = useState(getDefaultToDate()); // 오늘
  const [timelogs, setTimelogs] = useState([]);
  // 정렬 상태: { column: 'created_at', direction: 'desc' }
  const [sortConfig, setSortConfig] = useState({ column: 'created_at', direction: 'desc' });
  // 모바일 정렬 옵션: 'date' | 'time' | 'company'
  const [mobileSortOption, setMobileSortOption] = useState('date');
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [newRow, setNewRow] = useState(null);
  
  // 통합 검색 및 계약 관련 상태
  const [contractSearchQuery, setContractSearchQuery] = useState('');
  const [contractOptions, setContractOptions] = useState([]);
  const [recentContracts, setRecentContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);
  const [isSearchingContracts, setIsSearchingContracts] = useState(false);
  
  // 빠른 입력 모드 상태
  const [quickInputMode, setQuickInputMode] = useState(true);
  
  // localStorage에서 입력 정보 복원
  const loadQuickInputData = () => {
    try {
      const saved = localStorage.getItem('jobTimeInput_quickInputData');
      if (saved) {
        const parsed = JSON.parse(saved);
        // contract는 복원하지 않음 (객체이므로)
        return {
          log_date: parsed.log_date || format(new Date(), 'yyyy-MM-dd'),
          contract: null,
          work_description: parsed.work_description || '',
          job_category: parsed.job_category || '',
          input_minute: parsed.input_minute || '',
          input_description: parsed.input_description || '',
        };
      }
    } catch (e) {
      console.error('입력 정보 복원 실패:', e);
    }
    return {
      log_date: format(new Date(), 'yyyy-MM-dd'),
      contract: null,
      work_description: '',
      job_category: '',
      input_minute: '',
      input_description: '',
    };
  };
  
  const [quickInputData, setQuickInputData] = useState(loadQuickInputData());
  
  // 다중 행 입력 상태 관리
  const [inputRows, setInputRows] = useState([
    {
      id: Date.now(),
      log_date: format(new Date(), 'yyyy-MM-dd'),
      contract: null,
      work_description: '',
      job_category: '',
      input_minute: '',
      input_description: '',
    }
  ]);
  
  // 선택된 행 관리
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  
  // 삭제 확인 모달 상태
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmCallback, setDeleteConfirmCallback] = useState(null);
  const [deleteConfirmMessage, setDeleteConfirmMessage] = useState('');
  
  // 정보 모달 상태 (성공/알림 메시지)
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [infoModalMessage, setInfoModalMessage] = useState('');
  const [infoModalTitle, setInfoModalTitle] = useState('알림');
  
  // 입력 정보를 localStorage에 저장
  useEffect(() => {
    try {
      const dataToSave = {
        log_date: quickInputData.log_date,
        work_description: quickInputData.work_description,
        input_minute: quickInputData.input_minute,
        input_description: quickInputData.input_description,
      };
      localStorage.setItem('jobTimeInput_quickInputData', JSON.stringify(dataToSave));
    } catch (e) {
      console.error('입력 정보 저장 실패:', e);
    }
  }, [quickInputData.log_date, quickInputData.work_description, quickInputData.input_minute, quickInputData.input_description]);
  
  
  // 뷰 모드 상태 제거 (빠른 입력과 테이블을 동시에 표시)
  
  // 모바일 Bottom Sheet 상태
  const [bottomSheetOpen, setBottomSheetOpen] = useState(false);
  const [bottomSheetData, setBottomSheetData] = useState(null);
  
  // 모바일 To-Do Drawer 상태
  const [todoDrawerOpen, setTodoDrawerOpen] = useState(false);
  const [draggingTodo, setDraggingTodo] = useState(null);
  
  // 스마트 제안 상태
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // 신뢰도 확보 기능 상태
  const [missingDates, setMissingDates] = useState([]);
  const [streakData, setStreakData] = useState({ current_streak: 0, month_rate: 0 });
  const [showMissingAlert, setShowMissingAlert] = useState(false);
  
  // 테이블 관련 상태 - 퍼센트 기반 동적 너비
  const [tableWidth, setTableWidth] = useState(0);
  const [editingRowId, setEditingRowId] = useState(null);
  const [rowHeights, setRowHeights] = useState({});
  const [isDragging, setIsDragging] = useState(false);

  // 테이블 너비 동적 계산 (퍼센트 기반)
  const columnWidthPercentages = {
    timelog_id: 5,
    log_date: 8,  // 업무일자 - 너비 확대 (6 → 8)
    // company_code: 6,
    company_name: 9,
    contract_id: 7,
    contract_title: 12,  // 계약명칭 - 너비 줄임
    job_category: 8,  // 업무 구분 - 새로 추가
    work_description: 24, // 업무내용 상세 - 너비 확대 (20 → 24)
    input_minute: 9,  // 투입시간(분) - 너비 조정 (7 → 9, 헤더 텍스트가 잘리지 않도록)
    created_at: 12,  // 생성일시 - 내용이 라인을 넘어가지 않도록 너비 확보
    notes: 8,  // 비고 - 내용이 라인을 넘어가지 않도록 너비 확보
  };

  // 테이블 컨테이너 너비 계산 (사이드바 상태에 따라 동적 조정)
  useEffect(() => {
    const updateTableWidth = () => {
      const tableContainer = document.querySelector('[data-table-container="timelog"]');
      if (tableContainer) {
        setTableWidth(tableContainer.offsetWidth);
      }
    };

    // 초기 너비 설정
    updateTableWidth();

    // 리사이즈 이벤트 리스너
    window.addEventListener('resize', updateTableWidth);
    
    // 사이드바 토글 시에도 업데이트 (약간의 지연 후)
    const timer = setInterval(updateTableWidth, 100);

    return () => {
      window.removeEventListener('resize', updateTableWidth);
      clearInterval(timer);
    };
  }, []);

  // 최근 사용 계약 조회
  useEffect(() => {
    fetchRecentContracts();
  }, []);

  // 계약 검색 (디바운싱)
  useEffect(() => {
    if (!contractSearchQuery || contractSearchQuery.length < 2) {
      setContractOptions([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      searchContracts(contractSearchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [contractSearchQuery]);

  // 계약 선택 시 스마트 제안 조회
  useEffect(() => {
    if (selectedContract?.contract_id) {
      fetchSuggestions(selectedContract.contract_id);
    }
  }, [selectedContract]);

  // 투입시간 조회 함수 (다른 함수에서도 사용 가능)
  const fetchTimelogs = useCallback(async (customFromDate = null, customToDate = null) => {
    try {
      const startDate = customFromDate || fromDate;
      const endDate = customToDate || toDate;
      const params = { start_date: startDate, end_date: endDate };
      const response = await axios.get(`${API_BASE_URL}/timelogs/my`, { params });
      const data = response.data || [];
      // 디버깅: job_category 데이터 확인
      if (data.length > 0) {
        const firstRecord = data[0];
        console.log('[투입시간 조회] 첫 번째 레코드 전체:', firstRecord);
        console.log('[투입시간 조회] 첫 번째 레코드 키 목록:', Object.keys(firstRecord));
        console.log('[투입시간 조회] job_category 값:', firstRecord.job_category, '(타입:', typeof firstRecord.job_category, ')');
        console.log('[투입시간 조회] job_category가 undefined인가?', firstRecord.job_category === undefined);
        console.log('[투입시간 조회] job_category가 null인가?', firstRecord.job_category === null);
        console.log('[투입시간 조회] job_category가 빈 문자열인가?', firstRecord.job_category === '');
        
        // job_category가 있는 레코드 찾기
        const recordsWithJobCategory = data.filter(r => r.job_category && r.job_category.trim() !== '');
        console.log('[투입시간 조회] job_category가 있는 레코드 수:', recordsWithJobCategory.length, '/', data.length);
        if (recordsWithJobCategory.length > 0) {
          console.log('[투입시간 조회] job_category가 있는 첫 번째 레코드:', recordsWithJobCategory[0]);
        }
      }
      setTimelogs(data);
    } catch (error) {
      console.error('[투입시간 조회] 실패:', error);
      console.error('[투입시간 조회] 에러 상세:', error.response?.data || error.message);
      console.error('[투입시간 조회] 에러 상태 코드:', error.response?.status);
      console.error('[투입시간 조회] 에러 헤더:', error.response?.headers);
      setTimelogs([]); // 에러 시 빈 배열로 설정
    }
  }, [fromDate, toDate]);

  // 투입시간 자동 로딩 (컴포넌트 마운트 시 및 날짜 변경 시)
  // fromDate와 toDate가 초기값으로 설정되어 있으므로 페이지 로드 시 자동으로 조회됨
  useEffect(() => {
    fetchTimelogs();
  }, [fetchTimelogs]);

  // RightSidebar에서 To-Do 항목 추가 이벤트 수신
  useEffect(() => {
    const handleAddTodosToTimelog = async (event) => {
      const { todos: selectedTodos, companies: eventCompanies, contracts: eventContracts } = event.detail;
      if (!selectedTodos || selectedTodos.length === 0) return;

      console.log('[JobTimeInput] handleAddTodosToTimelog 호출됨');
      console.log('[JobTimeInput] 받은 todos:', selectedTodos);
      console.log('[JobTimeInput] 받은 companies:', eventCompanies);
      console.log('[JobTimeInput] 받은 contracts:', eventContracts);

      // RightSidebar에서 전달받은 companies와 contracts 사용
      // 만약 전달받지 못했으면 API로 직접 조회
      let companiesList = eventCompanies || [];
      let contractsList = eventContracts || [];
      
      // companies나 contracts가 비어있으면 API로 조회
      if (!companiesList || companiesList.length === 0 || !contractsList || contractsList.length === 0) {
        console.log('[JobTimeInput] companies 또는 contracts가 비어있어 API로 조회 시도');
        try {
          if (!companiesList || companiesList.length === 0) {
            const companiesResponse = await axios.get(`${API_BASE_URL}/admin/customers`);
            companiesList = companiesResponse.data || [];
            console.log('[JobTimeInput] API로 조회한 companies:', companiesList);
            console.log('[JobTimeInput] API로 조회한 companies 길이:', companiesList.length);
          }
          if (!contractsList || contractsList.length === 0) {
            const contractsResponse = await axios.get(`${API_BASE_URL}/admin/contracts`);
            contractsList = contractsResponse.data || [];
            console.log('[JobTimeInput] API로 조회한 contracts:', contractsList);
            console.log('[JobTimeInput] API로 조회한 contracts 길이:', contractsList.length);
          }
        } catch (error) {
          console.error('[JobTimeInput] API 조회 실패:', error);
        }
      }

      console.log('[JobTimeInput] 최종 companiesList:', companiesList);
      console.log('[JobTimeInput] 최종 contractsList:', contractsList);

      // To-Do 항목들을 기반으로 새 행들 생성 (API 조회 완료 후 처리)
      const newRows = selectedTodos.map((todo, index) => {
        console.log('[JobTimeInput] 처리 중인 todo:', todo);
        
        // contracts 배열에서 계약 찾기
        let contract = null;
        if (todo.contract_id) {
          contract = contractsList.find(c => c.contract_id === todo.contract_id);
          console.log('[JobTimeInput] contracts 배열에서 찾은 contract:', contract);
        }
        
        // companies 배열에서 업체 찾기
        const companyCode = contract?.company_code || todo.company_code;
        const company = companyCode ? companiesList.find(c => c.company_code === companyCode) : null;
        console.log('[JobTimeInput] companies 배열에서 찾은 company:', company);
        
        const companyName = company?.company_name_kr || '';
        
        // 계약 정보가 있으면 contract 객체 생성
        if (contract) {
          const contractTitle = contract.contract_title || '';
          const contractObj = {
            contract_id: contract.contract_id,
            company_code: companyCode,
            contract_title: contract.contract_title,
            company_name_kr: companyName,
            display_label: companyName && contractTitle
              ? `${companyName} - ${contractTitle}`.trim()
              : companyName || contractTitle || '',
          };
          
          console.log('[JobTimeInput] 생성된 contractObj:', contractObj);
          
          return {
            id: Date.now() + index,
            log_date: format(new Date(), 'yyyy-MM-dd'),
            contract: contractObj,
            work_description: todo.note_1 || '',
            job_category: todo.job_category || '',
            input_minute: '',
            input_description: '',
          };
        } else if (companyCode && company) {
          // 계약 정보는 없지만 업체 정보는 있는 경우
          const contractObj = {
            company_code: companyCode,
            contract_id: todo.contract_id || null,
            company_name_kr: companyName,
            contract_title: null,
            display_label: companyName || '',
          };
          
          console.log('[JobTimeInput] 생성된 contractObj (업체만):', contractObj);
          
          return {
            id: Date.now() + index,
            log_date: format(new Date(), 'yyyy-MM-dd'),
            contract: contractObj,
            work_description: todo.note_1 || '',
            job_category: todo.job_category || '',
            input_minute: '',
            input_description: '',
          };
        } else {
          // 업체 코드도 없는 경우
          console.log('[JobTimeInput] 업체 코드도 없음, contract 없이 행 생성');
          return {
            id: Date.now() + index,
            log_date: format(new Date(), 'yyyy-MM-dd'),
            contract: null,
            work_description: todo.note_1 || '',
            job_category: todo.job_category || '',
            input_minute: '',
            input_description: '',
          };
        }
      });

      console.log('[JobTimeInput] 생성된 newRows:', newRows);

      // 기존 inputRows에 새 행들 추가
      setInputRows(prev => {
        const updated = [...prev, ...newRows];
        console.log('[JobTimeInput] 업데이트된 inputRows:', updated);
        return updated;
      });
    };

    window.addEventListener('addTodosToTimelog', handleAddTodosToTimelog);
    return () => {
      window.removeEventListener('addTodosToTimelog', handleAddTodosToTimelog);
    };
  }, []);

  // 신뢰도 확보 기능 초기화
  useEffect(() => {
    fetchMissingDates();
    fetchStreak();
    
    // 출근 시간 캐치업: 어제 로그가 비어있으면 경고 표시
    const checkYesterdayMissing = async () => {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = format(yesterday, 'yyyy-MM-dd');
      
      try {
        const response = await axios.get(`${API_BASE_URL}/timelogs/my`, {
          params: { date: yesterdayStr }
        });
        if (!response.data || response.data.length === 0) {
          setShowMissingAlert(true);
        }
      } catch (error) {
        console.error('어제 로그 확인 실패:', error);
      }
    };
    
    // 오전 9시 이후에만 체크 (출근 시간 캐치업)
    const now = new Date();
    if (now.getHours() >= 9) {
      checkYesterdayMissing();
    }
  }, []);

  const fetchRecentContracts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/contracts/recent`, { params: { limit: 5 } });
      setRecentContracts(response.data || []);
    } catch (error) {
      console.error('최근 계약 조회 실패:', error);
    }
  };

  const searchContracts = async (query) => {
    if (!query || query.length < 2) return;
    
    setIsSearchingContracts(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/contracts/search`, { params: { q: query } });
      setContractOptions(response.data || []);
    } catch (error) {
      console.error('계약 검색 실패:', error);
      setContractOptions([]);
    } finally {
      setIsSearchingContracts(false);
    }
  };

  // 정렬된 timelogs 계산 (PC 및 모바일 공통)
  const sortedTimelogs = useMemo(() => {
    if (!timelogs || timelogs.length === 0) return [];
    
    const sorted = [...timelogs].sort((a, b) => {
      // 모바일 정렬 옵션 처리
      if (isMobileView) {
        if (mobileSortOption === 'date') {
          const aDate = a.log_date ? new Date(a.log_date).getTime() : 0;
          const bDate = b.log_date ? new Date(b.log_date).getTime() : 0;
          return bDate - aDate; // 최신순
        } else if (mobileSortOption === 'time') {
          const aTime = Number(a.input_minute) || 0;
          const bTime = Number(b.input_minute) || 0;
          return bTime - aTime; // 투입시간 내림차순
        } else if (mobileSortOption === 'company') {
          const aCompany = (a.company_name_kr || '').toLowerCase();
          const bCompany = (b.company_name_kr || '').toLowerCase();
          if (aCompany < bCompany) return -1;
          if (aCompany > bCompany) return 1;
          return 0;
        }
      }
      
      // PC 정렬 처리
      const { column, direction } = sortConfig;
      let aValue = a[column];
      let bValue = b[column];
      
      // null/undefined 처리
      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';
      
      // 날짜/시간 필드 처리
      if (column === 'created_at' || column === 'log_date') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      }
      
      // 숫자 필드 처리
      if (column === 'timelog_id' || column === 'input_minute') {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      }
      
      // 문자열 비교
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  }, [timelogs, sortConfig, isMobileView, mobileSortOption]);

  // 총 건수와 총 투입시간 합계 계산
  const summaryStats = useMemo(() => {
    const totalCount = sortedTimelogs.length;
    const totalMinutes = sortedTimelogs.reduce((sum, log) => sum + (log.input_minute || 0), 0);
    return {
      totalCount,
      totalMinutes
    };
  }, [sortedTimelogs]);

  // 정렬 핸들러
  const handleSort = (column) => {
    setSortConfig(prevConfig => {
      if (prevConfig.column === column) {
        // 같은 컬럼 클릭 시 방향 토글
        return {
          column,
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc'
        };
      } else {
        // 다른 컬럼 클릭 시 기본값은 내림차순
        return {
          column,
          direction: 'desc'
        };
      }
    });
  };

  // 분을 시간과 분으로 변환하는 함수 (예: 391 -> "6시간 31분")
  const formatMinutesToHoursAndMinutes = (minutes) => {
    const totalMinutes = Number(minutes) || 0;
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    return `${hours}시간 ${mins}분`;
  };

  // timelog ID를 연도(25)-6자리 숫자 형식으로 포맷팅
  const formatTimelogId = (id) => {
    if (!id) return '25-000000';
    // 현재 연도의 마지막 2자리 (2025 -> 25)
    const year = new Date().getFullYear().toString().slice(-2);
    // ID를 6자리 문자열로 변환 (앞에 0 패딩)
    const paddedId = String(id).padStart(6, '0');
    return `${year}-${paddedId}`;
  };

  // 엑셀 다운로드 함수
  const handleDownloadExcel = () => {
    if (sortedTimelogs.length === 0) {
      alert('다운로드할 데이터가 없습니다.');
      return;
    }

    // 엑셀 데이터 준비
    const excelData = sortedTimelogs.map((log, index) => ({
      '번호': index + 1,
      'ID': formatTimelogId(log.timelog_id),
      '업무일자': log.log_date || '',
      '업체코드': log.company_code || '',
      '업체명': log.company_name_kr || '',
      '계약번호': log.contract_id || '',
      '계약 명칭': log.contract_title || '',
      '업무 설명': log.work_description || '',
      '투입시간(분)': log.input_minute || 0,
      '생성일시': log.created_at ? format(new Date(log.created_at), 'yyyy-MM-dd HH:mm') : '',
      '비고': log.note_1 || ''
    }));

    // 워크북 생성
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);
    
    // 컬럼 너비 설정
    const colWidths = [
      { wch: 8 },   // 번호
      { wch: 8 },   // ID
      { wch: 15 },  // 업무일자 - 너비 확대 (12 → 15)
      { wch: 15 },  // 업체코드
      { wch: 20 },  // 업체명
      { wch: 18 },  // 계약번호
      { wch: 25 },  // 계약 명칭
      { wch: 25 },  // 업무 설명 - 너비 축소 (30 → 25)
      { wch: 15 },  // 투입시간(분)
      { wch: 18 },  // 생성일시
      { wch: 20 }   // 비고
    ];
    ws['!cols'] = colWidths;
    
    XLSX.utils.book_append_sheet(wb, ws, '투입시간 목록');
    
    // 파일명 생성: OnTimeLog_{사용자 이름}_투입시간_YYYYMMDD_hhmmss.xlsx
    const employeeName = localStorage.getItem('employeeName') || '사용자';
    const now = new Date();
    const dateStr = format(now, 'yyyyMMdd');
    const timeStr = format(now, 'HHmmss');
    const fileName = `OnTimeLog_${employeeName}_투입시간_${dateStr}_${timeStr}.xlsx`;
    
    // 파일 다운로드
    XLSX.writeFile(wb, fileName);
  };

  const fetchSuggestions = async (contractId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/timelogs/suggestions`, {
        params: { contract_id: contractId, limit: 10 }
      });
      setSuggestions(response.data || []);
    } catch (error) {
      console.error('제안 조회 실패:', error);
    }
  };


  // 다중 행 입력: 행 추가
  const handleAddRow = () => {
    const newRowId = Date.now();
    setInputRows(prev => [
      ...prev,
      {
        id: newRowId,
        log_date: format(new Date(), 'yyyy-MM-dd'),
        contract: null,
        work_description: '',
        job_category: '',
        input_minute: '',
        input_description: '',
      }
    ]);
    return newRowId;
  };

  // 다중 행 입력: 선택된 행 삭제
  const handleDeleteSelectedRows = () => {
    if (selectedRowIds.length === 0) {
      alert('삭제할 행을 선택해주세요.');
      return;
    }
    
    // 전체 선택 후 삭제하는 경우: 모든 행 삭제 후 빈 행 1개 자동 생성
    if (inputRows.length === selectedRowIds.length) {
      setDeleteConfirmMessage('모든 행을 삭제하시겠습니까? 빈 행 1개가 자동으로 생성됩니다.');
      setDeleteConfirmCallback(() => () => {
        setInputRows([
          {
            id: Date.now(),
            log_date: format(new Date(), 'yyyy-MM-dd'),
            contract: null,
            work_description: '',
            job_category: '',
            input_minute: '',
            input_description: '',
          }
        ]);
        setSelectedRowIds([]);
      });
      setDeleteConfirmOpen(true);
      return;
    }
    
    // 일부 행만 선택한 경우: 기존 로직 유지
    if (inputRows.length - selectedRowIds.length < 1) {
      alert('최소 1개의 행은 유지해야 합니다.');
      return;
    }
    
    setDeleteConfirmMessage(`선택한 ${selectedRowIds.length}개의 행을 삭제하시겠습니까?`);
    setDeleteConfirmCallback(() => () => {
      setInputRows(prev => prev.filter(row => !selectedRowIds.includes(row.id)));
      setSelectedRowIds([]);
    });
    setDeleteConfirmOpen(true);
  };
  
  // 삭제 확인 모달 핸들러
  const handleDeleteConfirm = () => {
    if (deleteConfirmCallback) {
      deleteConfirmCallback();
    }
    setDeleteConfirmOpen(false);
    setDeleteConfirmCallback(null);
    setDeleteConfirmMessage('');
  };
  
  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setDeleteConfirmCallback(null);
    setDeleteConfirmMessage('');
  };

  // 체크박스 선택/해제
  const handleRowSelect = (rowId) => {
    setSelectedRowIds(prev => 
      prev.includes(rowId) 
        ? prev.filter(id => id !== rowId)
        : [...prev, rowId]
    );
  };

  // 전체 선택/해제
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRowIds(inputRows.map(row => row.id));
    } else {
      setSelectedRowIds([]);
    }
  };

  // 다중 행 입력: 행 데이터 업데이트
  const handleRowChange = (rowId, field, value) => {
    setInputRows(prev => prev.map(row => 
      row.id === rowId ? { ...row, [field]: value } : row
    ));
  };

  // 날짜 증감 유틸리티
  const adjustDateByDays = (dateStr, delta) => {
    const baseDate = dateStr ? new Date(dateStr) : new Date();
    if (Number.isNaN(baseDate.getTime())) return dateStr || format(new Date(), 'yyyy-MM-dd');
    baseDate.setDate(baseDate.getDate() + delta);
    return format(baseDate, 'yyyy-MM-dd');
  };

  const handleRowDateStep = (rowId, delta) => {
    const targetRow = inputRows.find(row => row.id === rowId);
    const currentDate = targetRow?.log_date || format(new Date(), 'yyyy-MM-dd');
    const newDate = adjustDateByDays(currentDate, delta);
    handleRowChange(rowId, 'log_date', newDate);
  };

  const handleRangeDateStep = (target, delta) => {
    const currentDate = target === 'from' ? fromDate : toDate;
    const newDate = adjustDateByDays(currentDate, delta);
    if (target === 'from') {
      setFromDate(newDate);
    } else {
      setToDate(newDate);
    }
  };

  // 다중 행 입력: 계약 선택
  const handleRowContractSelect = (rowId, contract) => {
    setInputRows(prev => prev.map(row => 
      row.id === rowId ? { ...row, contract } : row
    ));
    // 계약 선택 시 제안 조회
    if (contract && contract.contract_id) {
      fetchSuggestions(contract.contract_id);
    }
  };

  // 다중 행 입력: 일괄 저장
  const handleBatchSubmit = async () => {
    // 유효성 검사
    const validRows = inputRows.filter(row => 
      row.contract && row.input_minute && parseInt(row.input_minute, 10) > 0
    );

    if (validRows.length === 0) {
      setInfoModalTitle('알림');
      setInfoModalMessage('저장할 데이터가 없습니다.\n계약과 투입시간을 입력해주세요.');
      setInfoModalOpen(true);
      return;
    }

    if (validRows.length !== inputRows.length) {
      const invalidCount = inputRows.length - validRows.length;
      if (!window.confirm(`${invalidCount}개의 행이 필수 항목을 입력하지 않아 제외됩니다. 계속하시겠습니까?`)) {
        return;
      }
    }

    try {
      // 데이터 변환
      const timelogDataList = validRows.map(row => {
        // work_description이 있으면 input_description 앞에 추가
        let inputDescription = row.input_description || '';
        let shouldSendWorkDescription = false;
        if (row.work_description) {
          if (inputDescription) {
            inputDescription = `${row.work_description}\n${inputDescription}`;
            shouldSendWorkDescription = false; // 이미 결합했으므로 전송하지 않음
          } else {
            inputDescription = row.work_description;
            shouldSendWorkDescription = true; // input_description이 없었으므로 전송
          }
        }

        // contract_id는 이미 validRows 필터링에서 확인되었으므로, 타입 변환만 수행
        const contractId = row.contract?.contract_id;
        if (!contractId && contractId !== 0) {
          // 이 경우는 validRows 필터링에서 이미 걸러졌어야 함
          console.error('❌ [배치 저장] contract_id가 없습니다. row:', row);
          throw new Error('계약을 선택해주세요.');
        }

        // contract_id는 숫자 또는 문자열 모두 허용 (예: 123 또는 "THE-C-2024-086")
        let finalContractId;
        if (typeof contractId === 'number') {
          finalContractId = contractId;
        } else if (typeof contractId === 'string' && contractId.trim() !== '') {
          // 문자열 형식의 contract_id도 허용 (예: "THE-C-2024-086")
          finalContractId = contractId.trim();
        } else {
          console.error('❌ [배치 저장] contract_id 타입 오류:', typeof contractId, contractId);
          throw new Error('계약 ID가 유효하지 않습니다.');
        }

        // input_minute도 NaN 방지
        const inputMinute = parseInt(row.input_minute, 10);
        if (isNaN(inputMinute) || inputMinute <= 0) {
          console.error('❌ [배치 저장] input_minute 오류:', row.input_minute);
          throw new Error('투입시간이 유효하지 않습니다.');
        }

        // company_code는 서버에서 필수로 요구하므로 포함
        const companyCode = row.contract?.company_code || row.contract?.company_code_kr;
        
        const jobCategoryValue = row.job_category?.trim() || null;
        console.log('[배치 저장] ===== job_category 값 확인 =====');
        console.log('[배치 저장] row 객체 전체:', row);
        console.log('[배치 저장] row.job_category 원본:', row.job_category);
        console.log('[배치 저장] row.job_category 타입:', typeof row.job_category);
        console.log('[배치 저장] row.job_category?.trim():', row.job_category?.trim());
        console.log('[배치 저장] 최종 jobCategoryValue:', jobCategoryValue);
        console.log('[배치 저장] jobCategoryValue 타입:', typeof jobCategoryValue);
        console.log('[배치 저장] ================================');
        
        return {
          log_date: row.log_date,
          company_code: companyCode, // 서버 필수 필드
          contract_id: finalContractId,
          job_category: jobCategoryValue,
          work_description: shouldSendWorkDescription ? row.work_description : undefined,
          input_minute: inputMinute,
          input_description: inputDescription || undefined,
          notes: row.input_description || undefined,
        };
      });

      // timelogDataList가 비어있는지 확인
      if (!timelogDataList || timelogDataList.length === 0) {
        console.error('❌ [배치 저장] timelogDataList가 비어있습니다.');
        setInfoModalTitle('입력 오류');
        setInfoModalMessage('저장할 데이터가 없습니다.');
        setInfoModalOpen(true);
        return;
      }

      // Zod 검증 적용
      const validation = validateDataWithAllErrors(TimelogBatchCreateSchema, timelogDataList);
      
      if (!validation.success) {
        setInfoModalTitle('입력 오류');
        setInfoModalMessage(`입력 데이터 검증 실패:\n${validation.errors?.join('\n') || '알 수 없는 오류가 발생했습니다.'}`);
        setInfoModalOpen(true);
        return;
      }

      // 검증된 데이터 확인
      if (!validation.data || !Array.isArray(validation.data)) {
        console.error('❌ [배치 저장] 검증된 데이터 형식 오류:', validation.data);
        setInfoModalTitle('입력 오류');
        setInfoModalMessage('검증된 데이터 형식이 올바르지 않습니다.');
        setInfoModalOpen(true);
        return;
      }

      // 검증된 데이터로 일괄 저장 API 호출
      const response = await axios.post(`${API_BASE_URL}/timelogs/batch`, validation.data);
      
      // 입력한 날짜 범위 확인
      const dates = validRows.map(row => row.log_date);
      const minDate = Math.min(...dates.map(d => new Date(d).getTime()));
      const maxDate = Math.max(...dates.map(d => new Date(d).getTime()));
      const minDateStr = format(new Date(minDate), 'yyyy-MM-dd');
      const maxDateStr = format(new Date(maxDate), 'yyyy-MM-dd');

      // 조회 범위 조정
      if (minDateStr < fromDate) {
        setFromDate(minDateStr);
      }
      if (maxDateStr > toDate) {
        setToDate(maxDateStr);
      }

      // 조회 범위에 포함되어 있으면 즉시 조회
      if (minDateStr >= fromDate && maxDateStr <= toDate) {
        fetchTimelogs();
      }

      // 성공 후 초기화
      setInputRows([
        {
          id: Date.now(),
          log_date: format(new Date(), 'yyyy-MM-dd'),
          contract: null,
          work_description: '',
          input_minute: '',
          input_description: '',
        }
      ]);

      // 최근 계약 목록 갱신
      fetchRecentContracts();
      
      // 스트릭 갱신
      fetchStreak();
      
      // 오늘 업무시간 업데이트 이벤트 발생
      window.dispatchEvent(new Event('timelogUpdated'));
      
      // 성공 메시지
      const message = response.data?.message || `${validRows.length}개의 투입시간이 등록되었습니다.`;
      if (response.data?.errors && response.data.errors.length > 0) {
        setInfoModalTitle('저장 완료');
        setInfoModalMessage(`${message}\n\n오류:\n${response.data.errors.join('\n')}`);
        setInfoModalOpen(true);
      } else {
        setInfoModalTitle('저장 완료');
        setInfoModalMessage(message);
        setInfoModalOpen(true);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || '저장 실패';
      setInfoModalTitle('오류');
      setInfoModalMessage(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      setInfoModalOpen(true);
    }
  };

  // 빠른 입력 처리 (기존 함수 유지 - 모바일용)
  const handleQuickInput = async () => {
    if (!quickInputData.contract || !quickInputData.input_minute) {
      setInfoModalTitle('알림');
      setInfoModalMessage('계약과 투입시간은 필수 입력 항목입니다.');
      setInfoModalOpen(true);
      return;
    }

    try {
      // work_description이 있으면 input_description 앞에 추가
      let inputDescription = quickInputData.input_description || '';
      let shouldSendWorkDescription = false;
      if (quickInputData.work_description) {
        if (inputDescription) {
          inputDescription = `${quickInputData.work_description}\n${inputDescription}`;
          shouldSendWorkDescription = false; // 이미 결합했으므로 전송하지 않음
        } else {
          inputDescription = quickInputData.work_description;
          shouldSendWorkDescription = true; // input_description이 없었으므로 전송
        }
      }

      // contract_id는 이미 위에서 확인되었으므로, 타입 변환만 수행
      const contractId = quickInputData.contract?.contract_id;
      if (!contractId && contractId !== 0) {
        console.error('❌ [빠른 입력] contract_id가 없습니다.');
        setInfoModalTitle('입력 오류');
        setInfoModalMessage('계약을 선택해주세요.');
        setInfoModalOpen(true);
        return;
      }

      // contract_id는 숫자 또는 문자열 모두 허용 (예: 123 또는 "THE-C-2024-086")
      let finalContractId;
      if (typeof contractId === 'number') {
        finalContractId = contractId;
      } else if (typeof contractId === 'string' && contractId.trim() !== '') {
        // 문자열 형식의 contract_id도 허용 (예: "THE-C-2024-086")
        finalContractId = contractId.trim();
      } else {
        console.error('❌ [빠른 입력] contract_id 타입 오류:', typeof contractId, contractId);
        setInfoModalTitle('입력 오류');
        setInfoModalMessage('계약 ID가 유효하지 않습니다.');
        setInfoModalOpen(true);
        return;
      }

      // input_minute도 NaN 방지
      const inputMinute = parseInt(quickInputData.input_minute, 10);
      if (isNaN(inputMinute) || inputMinute <= 0) {
        console.error('❌ [빠른 입력] input_minute 오류:', quickInputData.input_minute);
        setInfoModalTitle('입력 오류');
        setInfoModalMessage('투입시간이 유효하지 않습니다.');
        setInfoModalOpen(true);
        return;
      }

      // company_code는 서버에서 필수로 요구하므로 포함
      const companyCode = quickInputData.contract?.company_code || quickInputData.contract?.company_code_kr;

      const jobCategoryValue = quickInputData.job_category?.trim() || null;
      console.log('[빠른 입력] ===== job_category 값 확인 =====');
      console.log('[빠른 입력] quickInputData 객체 전체:', quickInputData);
      console.log('[빠른 입력] quickInputData.job_category 원본:', quickInputData.job_category);
      console.log('[빠른 입력] quickInputData.job_category 타입:', typeof quickInputData.job_category);
      console.log('[빠른 입력] quickInputData.job_category?.trim():', quickInputData.job_category?.trim());
      console.log('[빠른 입력] 최종 jobCategoryValue:', jobCategoryValue);
      console.log('[빠른 입력] jobCategoryValue 타입:', typeof jobCategoryValue);
      console.log('[빠른 입력] ================================');
      
      const timelogData = {
        log_date: quickInputData.log_date,
        company_code: companyCode, // 서버 필수 필드
        contract_id: finalContractId,
        job_category: jobCategoryValue,
        work_description: shouldSendWorkDescription ? quickInputData.work_description : undefined,
        input_minute: inputMinute,
        input_description: inputDescription || undefined,
        notes: quickInputData.input_description || undefined,
      };

      // Zod 검증 적용
      const validation = validateData(TimelogCreateSchema, timelogData);
      
      if (!validation.success) {
        setInfoModalTitle('입력 오류');
        setInfoModalMessage(validation.error);
        setInfoModalOpen(true);
        return;
      }

      // 검증된 데이터로 API 호출
      await axios.post(`${API_BASE_URL}/timelogs`, validation.data);
      
      // 입력한 날짜가 현재 조회 범위에 포함되지 않으면 조회 범위 조정
      const inputDate = quickInputData.log_date;
      if (inputDate < fromDate || inputDate > toDate) {
        // 입력한 날짜를 포함하도록 조회 범위 조정
        if (inputDate < fromDate) {
          setFromDate(inputDate);
        }
        if (inputDate > toDate) {
          setToDate(inputDate);
        }
        // useEffect가 fromDate/toDate 변경을 감지하여 자동으로 조회하므로
        // 여기서는 조회 범위만 조정하고 fetchTimelogs는 호출하지 않음
      } else {
        // 조회 범위에 포함되어 있으면 즉시 조회
        fetchTimelogs();
      }
      
      // 성공 후 초기화 (날짜는 유지, 나머지만 초기화)
      const resetData = {
        log_date: format(new Date(), 'yyyy-MM-dd'), // 오늘 날짜로 초기화
        contract: null,
        work_description: '',
        job_category: '',
        input_minute: '',
        input_description: '',
      };
      setQuickInputData(resetData);
      setSelectedContract(null);
      setContractSearchQuery('');
      
      // localStorage에서도 입력 정보 업데이트
      try {
        const dataToSave = {
          log_date: resetData.log_date,
          work_description: '',
          job_category: '',
          input_minute: '',
          input_description: '',
        };
        localStorage.setItem('jobTimeInput_quickInputData', JSON.stringify(dataToSave));
      } catch (e) {
        console.error('입력 정보 저장 실패:', e);
      }
      
      // 오늘 업무시간 업데이트 이벤트 발생
      window.dispatchEvent(new Event('timelogUpdated'));
      
      // 최근 계약 목록 갱신
      fetchRecentContracts();
      
      // 투입시간 목록 갱신은 위에서 처리됨 (조회 범위 조정 시 useEffect가 자동 조회)
      fetchStreak(); // 스트릭 갱신
      
      setInfoModalTitle('저장 완료');
      setInfoModalMessage('투입시간이 등록되었습니다.');
      setInfoModalOpen(true);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || '저장 실패';
      setInfoModalTitle('오류');
      setInfoModalMessage(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      setInfoModalOpen(true);
    }
  };


  // 제안 선택 핸들러
  const handleSuggestionSelect = (suggestion) => {
    setQuickInputData(prev => ({
      ...prev,
      work_description: suggestion.work_description,
      input_minute: Math.round(suggestion.avg_minutes || 0).toString(),
    }));
    setShowSuggestions(false);
  };

  // 누락 날짜 조회
  const fetchMissingDates = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/timelogs/missing-dates`, {
        params: { days: 7 }
      });
      setMissingDates(response.data.missing_dates || []);
    } catch (error) {
      console.error('누락 날짜 조회 실패:', error);
    }
  };

  // 입력 스트릭 조회
  const fetchStreak = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/timelogs/streak`);
      setStreakData(response.data || { current_streak: 0, month_rate: 0 });
    } catch (error) {
      console.error('스트릭 조회 실패:', error);
    }
  };

  // 계약 선택 핸들러
  const handleContractSelect = (contract) => {
    setSelectedContract(contract);
    setQuickInputData(prev => ({ ...prev, contract }));
    setContractSearchQuery(contract?.display_label || '');
  };

  // 통합 검색 옵션 (최근 사용 + 검색 결과)
  const allContractOptions = useMemo(() => {
    const recent = recentContracts.map(c => ({ ...c, isRecent: true }));
    const search = contractOptions.filter(
      opt => !recentContracts.some(rc => rc.contract_id === opt.contract_id)
    );
    
    if (recent.length > 0 && search.length > 0) {
      return [
        ...recent,
        { divider: true },
        ...search,
      ];
    }
    return recent.length > 0 ? recent : search;
  }, [recentContracts, contractOptions]);

  const handleCellClick = (rowId, field, value) => {
    // 편집 가능한 필드만 허용
    const editableFields = ['log_date', 'work_description', 'input_minute'];
    if (!editableFields.includes(field)) {
      return; // 편집 불가능한 필드는 무시
    }
    
    // 수정 모드일 때만 셀 편집 가능
    if (editingRowId === rowId) {
      setEditingCell({ rowId, field });
      setEditValue(value || '');
    }
  };

  const handleCellBlur = async () => {
    if (!editingCell) return;
    
    const { rowId, field } = editingCell;
    const row = timelogs.find(r => r.timelog_id === rowId);
    
    if (!row || row[field] === editValue) {
      setEditingCell(null);
      return;
    }

    try {
      let updateData;
      
      // work_description을 수정한 경우 input_description으로 변환
      if (field === 'work_description') {
        // work_description(첫 줄) + input_description_detail(나머지 줄)
        const detailPart = row.input_description_detail || '';
        const newInputDescription = detailPart 
          ? `${editValue}\n${detailPart}`
          : editValue;
        
        updateData = { 
          ...row, 
          work_description: editValue,
          input_description: newInputDescription 
        };
      } else {
        // 다른 필드는 그대로 업데이트
        updateData = { ...row, [field]: editValue };
      }
      
      // Zod 검증 적용 (업데이트는 선택적 필드)
      const validation = validateData(TimelogUpdateSchema, updateData);
      
      if (!validation.success) {
        alert(validation.error);
        setEditingCell(null);
        return;
      }
      
      // 검증된 데이터로 API 호출
      await axios.put(`${API_BASE_URL}/timelogs/${rowId}`, validation.data);
      setTimelogs(prev => prev.map(r => r.timelog_id === rowId ? { ...r, [field]: editValue } : r));
      
      // 목록 새로고침 및 이벤트 발생 (화면 업데이트 보장)
      fetchTimelogs();
      window.dispatchEvent(new Event('timelogUpdated'));
    } catch (error) {
      alert(error.response?.data?.detail || '수정 실패');
    }
    
    setEditingCell(null);
  };

  const handleEditClick = (rowId) => {
    setEditingRowId(rowId);
  };

  // 더블클릭 또는 F2로 수정모드 진입
  const handleRowDoubleClick = (rowId) => {
    setEditingRowId(rowId);
    // 첫 번째 편집 가능한 필드(업무일자)로 자동 진입
    setTimeout(() => {
      const row = timelogs.find(r => r.timelog_id === rowId);
      if (row) {
        handleCellClick(rowId, 'log_date', row.log_date);
      }
    }, 50);
  };

  // F2 키로 편집 모드 진입 (페이지 레벨)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // 모달이 열려 있으면 무시
      if (bottomSheetOpen) {
        return;
      }
      
      if (e.key === 'F2') {
        e.preventDefault();
        
        // 이미 편집 모드인 경우: 첫 번째 셀에 커서 활성화
        if (editingRowId) {
          const row = timelogs.find(r => r.timelog_id === editingRowId);
          if (row) {
            // 첫 번째 편집 가능한 필드로 진입
            handleCellClick(editingRowId, 'log_date', row.log_date);
          }
          return;
        }
        
        // 편집 모드가 아닌 경우: 행을 편집 모드로 전환
        // 우선순위 1: 포커스된 행이 있으면 해당 행 편집
        const focusedRow = document.activeElement.closest('[data-row-id]');
        if (focusedRow) {
          const rowId = parseInt(focusedRow.getAttribute('data-row-id'), 10);
          if (rowId && timelogs.find(r => r.timelog_id === rowId)) {
            setEditingRowId(rowId);
            return;
          }
        }
        
        // 우선순위 2: 첫 번째 투입시간이 있으면 편집 모드로
        if (timelogs.length > 0) {
          setEditingRowId(timelogs[0].timelog_id);
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editingRowId, bottomSheetOpen, timelogs]);

  // 탭 키로 다음/이전 편집 가능한 필드로 이동
  const handleFieldTab = (rowId, currentField, direction = 'next') => {
    const editableFields = ['log_date', 'work_description', 'input_minute'];
    const currentIndex = editableFields.indexOf(currentField);
    
    if (direction === 'prev') {
      // 이전 필드로 이동 (Shift+Tab)
      if (currentIndex > 0) {
        const prevField = editableFields[currentIndex - 1];
        const row = timelogs.find(r => r.timelog_id === rowId);
        if (row) {
          handleCellClick(rowId, prevField, row[prevField]);
        }
      }
    } else {
      // 다음 필드로 이동 (Tab)
      if (currentIndex < editableFields.length - 1) {
        const nextField = editableFields[currentIndex + 1];
        const row = timelogs.find(r => r.timelog_id === rowId);
        if (row) {
          handleCellClick(rowId, nextField, row[nextField]);
        }
      } else {
        // 마지막 필드면 저장
        handleSaveClick(rowId);
      }
    }
  };

/*   const handleSaveClick = async (rowId) => {
    const row = timelogs.find(r => r.timelog_id === rowId);
    if (!row) return;

    try {
      await axios.put(`${API_BASE_URL}/timelogs/${rowId}`, row);
      setEditingRowId(null);
      fetchTimelogs();
      alert('저장되었습니다.');
    } catch (error) {
      alert(error.response?.data?.detail || '저장 실패');
    }
  }; */

  const handleSaveClick = async (rowId, silent = false) => {
    let rowToSave = timelogs.find(r => r.timelog_id === rowId);
    if (!rowToSave) return;
  
    // 편집 중인 셀이 있으면 editValue를 먼저 반영
    if (editingCell && editingCell.rowId === rowId) {
      const { field } = editingCell;
      
      // work_description을 수정한 경우 input_description으로 변환
      if (field === 'work_description') {
        // work_description(첫 줄) + input_description_detail(나머지 줄)
        const detailPart = rowToSave.input_description_detail || '';
        const newInputDescription = detailPart 
          ? `${editValue}\n${detailPart}`
          : editValue;
        
        rowToSave = { 
          ...rowToSave, 
          work_description: editValue,
          input_description: newInputDescription 
        };
      } else {
        // 다른 필드는 그대로 업데이트
        rowToSave = { ...rowToSave, [field]: editValue };
      }
      
      // timelogs 상태 업데이트
      setTimelogs(prev => prev.map(r => 
        r.timelog_id === rowId ? rowToSave : r
      ));
      
      // 편집 모드 해제
      setEditingCell(null);
    }
  
    try {
      // Zod 검증 적용 (업데이트는 선택적 필드)
      const validation = validateData(TimelogUpdateSchema, rowToSave);
      
      if (!validation.success) {
        alert(validation.error);
        return;
      }
      
      // 검증된 데이터로 API 호출
      await axios.put(`${API_BASE_URL}/timelogs/${rowId}`, validation.data);
      setEditingRowId(null);
      fetchTimelogs();
      // 오늘 업무시간 업데이트 이벤트 발생
      window.dispatchEvent(new Event('timelogUpdated'));
      // alert 제거 (silent 파라미터는 향후 확장용)
    } catch (error) {
      alert(error.response?.data?.detail || '저장 실패');
    }
  };

  const handleCancelEdit = () => {
    setEditingRowId(null);
    setEditingCell(null);
  };

  const handleDelete = (timelogId) => {
    // 삭제 확인 모달 표시
    setDeleteConfirmMessage('정말 삭제하시겠습니까?');
    setDeleteConfirmCallback(() => async () => {
      try {
        await axios.delete(`${API_BASE_URL}/timelogs/${timelogId}`);
        fetchTimelogs();
        // 오늘 업무시간 업데이트 이벤트 발생
        window.dispatchEvent(new Event('timelogUpdated'));
        setDeleteConfirmOpen(false);
      } catch (error) {
        setDeleteConfirmOpen(false);
        setInfoModalTitle('오류');
        setInfoModalMessage(error.response?.data?.detail || '삭제 실패');
        setInfoModalOpen(true);
      }
    });
    setDeleteConfirmOpen(true);
  };

  // 컬럼 너비 계산 함수 (퍼센트 기반)
  const getColumnWidth = useCallback((columnKey) => {
    if (tableWidth === 0) return 'auto';
    const percentage = columnWidthPercentages[columnKey] || 8;
    return `${(tableWidth * percentage) / 100}px`;
  }, [tableWidth]);

  const handleRowHeightChange = useCallback((height, rowId) => {
    setRowHeights(prev => ({ ...prev, [rowId]: height }));
  }, []);

  const renderEditableCell = (row, field, value, type = 'text') => {
    const isEditing = editingCell?.rowId === row.timelog_id && editingCell?.field === field;
    const isRowEditing = editingRowId === row.timelog_id;
    
    // 편집 가능한 필드 체크
    const editableFields = ['log_date', 'work_description', 'input_minute'];
    const isFieldEditable = editableFields.includes(field);
    
    // 수정 모드가 아니거나 편집 불가능한 필드면 텍스트만 표시
    if (!isRowEditing || !isFieldEditable) {
      return <Box>{value || '-'}</Box>;
    }
    
    if (isEditing) {
      if (field === 'log_date') {
        return (
          <TextField
            type="date"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleCellBlur}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleCellBlur();
                // 값 반영 후 저장
                setTimeout(() => handleSaveClick(row.timelog_id), 50);
              }
              if (e.key === 'Tab') {
                e.preventDefault();
                handleCellBlur();
                // Shift+Tab: 이전 필드로 이동, Tab: 다음 필드로 이동
                const direction = e.shiftKey ? 'prev' : 'next';
                setTimeout(() => handleFieldTab(row.timelog_id, field, direction), 50);
              }
              if (e.key === 'Escape') setEditingCell(null);
            }}
            autoFocus
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ 
              width: '100%', 
              fontSize: '14px', 
              '& .MuiInputBase-input': { fontSize: '14px' },
              '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                filter: isDark ? 'invert(1)' : 'none',
                opacity: 1,
                cursor: 'pointer',
              },
              '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator:hover': {
                opacity: 0.7,
              },
            }}
            inputProps={{
              sx: {
                color: colors.primaryText,
                '&::-webkit-calendar-picker-indicator': {
                  filter: isDark ? 'invert(1)' : 'none',
                },
              }
            }}
          />
        );
      } else if (field === 'input_minute') {
        return (
          <TextField
            type="number"
            value={editValue}
            onChange={(e) => {
              const value = e.target.value;
              // 빈 문자열 허용 (입력 중일 때)
              if (value === '' || value === null || value === undefined) {
                setEditValue('');
                return;
              }
              // 숫자만 추출 (소수점 제거, 음수 방지)
              const numValue = parseInt(value, 10);
              if (!isNaN(numValue) && numValue > 0) {
                setEditValue(numValue.toString());
              } else if (value === '') {
                setEditValue('');
              }
              // 음수나 0이 입력되면 무시
            }}
            onBlur={(e) => {
              // 포커스 해제 시 값이 없거나 0 이하면 1로 설정 후 저장
              const value = parseInt(e.target.value, 10);
              if (isNaN(value) || value <= 0) {
                setEditValue('1');
                setTimeout(() => handleCellBlur(), 0);
              } else {
                handleCellBlur();
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const value = parseInt(editValue, 10);
                if (isNaN(value) || value <= 0) {
                  setEditValue('1');
                }
                handleCellBlur();
                // 값 반영 후 저장
                setTimeout(() => handleSaveClick(row.timelog_id), 50);
              }
              if (e.key === 'Tab') {
                e.preventDefault();
                const value = parseInt(editValue, 10);
                if (isNaN(value) || value <= 0) {
                  setEditValue('1');
                }
                handleCellBlur();
                // 값 반영 후 다음 필드로 이동 (마지막 필드이므로 저장됨)
                setTimeout(() => handleFieldTab(row.timelog_id, field), 50);
              }
              if (e.key === 'Escape') setEditingCell(null);
            }}
            autoFocus
            size="small"
            inputProps={{
              min: 1,
              step: 1,
            }}
            InputProps={{
              endAdornment: <InputAdornment position="end">분</InputAdornment>,
            }}
            sx={{ width: '100%', fontSize: '14px', '& .MuiInputBase-input': { fontSize: '14px' } }}
          />
        );
      } else {
        return (
          <TextField
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleCellBlur}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                // Shift+Enter는 줄바꿈, Enter만 누르면 저장
                e.preventDefault();
                handleCellBlur();
                // 값 반영 후 저장
                setTimeout(() => handleSaveClick(row.timelog_id), 50);
              }
              if (e.key === 'Tab') {
                e.preventDefault();
                handleCellBlur();
                // 값 반영 후 다음 필드로 이동
                setTimeout(() => handleFieldTab(row.timelog_id, field), 50);
              }
              if (e.key === 'Escape') setEditingCell(null);
            }}
            autoFocus
            size="small"
            fullWidth
            multiline={field === 'input_description' || field === 'work_description'}
            rows={field === 'input_description' || field === 'work_description' ? 2 : 1}
            sx={{ fontSize: '14px', '& .MuiInputBase-input': { fontSize: '14px' } }}
          />
        );
      }
    }
    
    // 편집 가능한 필드만 클릭 가능 (이미 위에서 선언된 editableFields 재사용)
    const canEdit = isRowEditing && isFieldEditable;
    
    return (
      <Box
        onClick={() => canEdit && handleCellClick(row.timelog_id, field, value)}
        sx={{
          width: '100%',
          minHeight: '32px',
          padding: '8px',
          cursor: canEdit ? 'pointer' : 'default',
          borderRadius: '4px',
          fontSize: '14px',
          // 편집 가능한 필드에 음영 효과 추가
          backgroundColor: canEdit 
            ? (isDark ? 'rgba(138,124,255,0.1)' : 'rgba(138,124,255,0.05)')
            : 'transparent',
          border: canEdit 
            ? `1px dashed ${colors.accent}` 
            : '1px solid transparent',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: canEdit 
              ? (isDark ? 'rgba(138,124,255,0.15)' : 'rgba(138,124,255,0.1)')
              : 'transparent',
          },
        }}
      >
        {field === 'log_date' && value ? format(new Date(value), 'yyyy-MM-dd') : value || '-'}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 1.5, pt: 4, minHeight: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>

      {/* 빠른 입력 섹션 (PC 버전) */}
      {!isMobileView && (
        <Card sx={{ mb: 2, backgroundColor: isDark ? '#1C1D22' : '#F8F9FA' }}>
          <CardContent>
            <Box>
              {/* 입력 스트릭 표시 및 출근 시간 캐치업 경고 - 한 줄에 나란히 배치 */}
              {(streakData.current_streak > 0 || showMissingAlert) && (
                <Box sx={{ 
                  mb: 2, 
                  p: 1.5, 
                  backgroundColor: isDark ? '#2A2B30' : '#FFF3E0', 
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap'
                }}>
                  {streakData.current_streak > 0 && (
                    <Typography variant="body2" sx={{ fontWeight: 600, color: colors.accent }}>
                      🔥 {streakData.current_streak}일 연속 입력 달성!
                    </Typography>
                  )}
                  {showMissingAlert && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: colors.warning || '#FF9800' }}>
                        ⚠️ 어제({format(new Date(Date.now() - 86400000), 'yyyy-MM-dd')})의 로그가 비어있습니다. 빠른 입력 섹션에서 어제 날짜를 선택하여 입력해주세요.
                      </Typography>
                      <IconButton 
                        size="small" 
                        onClick={() => setShowMissingAlert(false)}
                        sx={{ ml: 'auto' }}
                      >
                        <X size={16} />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              )}

              {/* 상단 액션 바 - 타이틀과 모든 버튼을 한 줄에 배치 */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Clock size={18} />
                  <Typography variant="h6" sx={{ color: colors.primaryText, fontWeight: 600 }}>
                    투입시간 입력
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.accent, fontWeight: 600, ml: 1 }}>
                    {formatMinutesToHoursAndMinutes(
                      inputRows.reduce((sum, row) => sum + (parseInt(row.input_minute, 10) || 0), 0)
                    )}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="body2" sx={{ color: colors.secondaryText, minWidth: 100 }}>
                    {selectedRowIds.length}개 행 선택됨
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<Plus size={16} />}
                    onClick={handleAddRow}
                    size="small"
                    sx={{ 
                      borderColor: colors.accent,
                      color: colors.accent,
                      height: '32px',
                    }}
                  >
                    추가
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Trash2 size={16} />}
                    onClick={handleDeleteSelectedRows}
                    disabled={selectedRowIds.length === 0}
                    size="small"
                    sx={{
                      borderColor: selectedRowIds.length === 0 ? colors.secondaryText : (colors.error || '#f44336'),
                      color: selectedRowIds.length === 0 ? colors.secondaryText : (colors.error || '#f44336'),
                      height: '32px',
                      opacity: selectedRowIds.length === 0 ? 0.5 : 1,
                      '&:hover': {
                        borderColor: selectedRowIds.length === 0 ? colors.secondaryText : (colors.error || '#f44336'),
                        backgroundColor: selectedRowIds.length === 0 ? 'transparent' : (isDark ? 'rgba(244, 67, 54, 0.1)' : 'rgba(244, 67, 54, 0.05)'),
                      },
                    }}
                  >
                    삭제
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      const allSelected = inputRows.length > 0 && selectedRowIds.length === inputRows.length;
                      handleSelectAll(!allSelected);
                    }}
                    size="small"
                    sx={{
                      borderColor: colors.secondaryText,
                      color: colors.secondaryText,
                      height: '32px',
                      fontSize: '12px',
                    }}
                  >
                    {inputRows.length > 0 && selectedRowIds.length === inputRows.length ? '전체 해제' : '전체 선택'}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Save size={16} />}
                    onClick={handleBatchSubmit}
                    sx={{
                      backgroundColor: colors.accent,
                      minWidth: 100,
                      height: '32px',
                      fontSize: '14px',
                      fontWeight: 600,
                    }}
                  >
                    저장
                  </Button>
                </Box>
              </Box>

              {/* 다중 행 입력 테이블 - 하나의 박스 안에 모든 행 표시 */}
              <Box 
                sx={{ 
                  mb: 2,
                  p: 2,
                  backgroundColor: isDark ? 'transparent' : '#F8F9FA',
                  borderRadius: 2,
                  border: `1px solid ${colors.primaryBorder}`,
                }}
              >
                {inputRows.map((row, index) => {
                  // row.contract가 있으면 options에 포함시켜서 Autocomplete가 인식하도록 함
                  const baseOptions = [
                    ...(recentContracts.map(c => {
                      const companyName = c.company_name_kr || '';
                      const contractTitle = c.contract_title || '';
                      return {
                        ...c,
                        isRecent: true,
                        display_label: contractTitle && companyName
                          ? `${companyName} - ${contractTitle}`.trim()
                          : companyName || contractTitle || ''
                      };
                    })),
                    ...(recentContracts.length > 0 && contractOptions.length > 0 ? [{ divider: true }] : []),
                    ...(contractOptions.map(c => {
                      const companyName = c.company_name_kr || '';
                      const contractTitle = c.contract_title || '';
                      return {
                        ...c,
                        display_label: contractTitle && companyName
                          ? `${companyName} - ${contractTitle}`.trim()
                          : companyName || contractTitle || ''
                      };
                    }))
                  ];
                  
                  // row.contract가 있고 options에 없으면 추가
                  const rowContractOptions = row.contract && !baseOptions.find(opt => 
                    opt.contract_id === row.contract?.contract_id && opt.company_code === row.contract?.company_code
                  ) 
                    ? [row.contract, ...(baseOptions.length > 0 ? [{ divider: true }] : []), ...baseOptions]
                    : baseOptions;
                  const isSelected = selectedRowIds.includes(row.id);

                  return (
                    <Box
                      key={row.id}
                      sx={{
                        display: 'flex',
                        gap: 1.5,
                        alignItems: 'flex-start',
                        p: 1.5,
                        backgroundColor: isSelected 
                          ? (isDark ? 'transparent' : 'rgba(0, 0, 0, 0.05)') 
                          : 'transparent',
                        borderRadius: 1,
                        border: 'none',
                        transition: 'all 0.2s',
                        borderBottom: index < inputRows.length - 1 ? `1px solid ${colors.primaryBorder}` : 'none',
                        '&:hover': {
                          backgroundColor: isSelected 
                            ? (isDark ? 'transparent' : 'rgba(0, 0, 0, 0.08)') 
                            : 'transparent',
                        },
                      }}
                    >
                      {/* 체크박스 */}
                      <Box sx={{ pt: 0.5 }}>
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleRowSelect(row.id)}
                          size="small"
                          sx={{
                            color: colors.accent,
                            '&.Mui-checked': {
                              color: colors.accent,
                            },
                          }}
                        />
                      </Box>

                      {/* 입력 필드들 */}
                      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'nowrap', flex: 1, alignItems: 'flex-start' }}>
                        {/* 날짜 */}
                        <TextField
                          type="date"
                          label="업무일자"
                          value={row.log_date}
                          onChange={(e) => handleRowChange(row.id, 'log_date', e.target.value)}
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end" sx={{ mr: -0.5 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRowDateStep(row.id, 1)}
                                    sx={{ p: 0.25, color: colors.secondaryText }}
                                    aria-label="하루 뒤로"
                                  >
                                    <ChevronUp size={14} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRowDateStep(row.id, -1)}
                                    sx={{ p: 0.25, color: colors.secondaryText }}
                                    aria-label="하루 앞으로"
                                  >
                                    <ChevronDown size={14} />
                                  </IconButton>
                                </Box>
                              </InputAdornment>
                            ),
                          }}
                          sx={{ 
                            minWidth: 140, 
                            flexShrink: 0,
                            '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                              filter: isDark ? 'invert(1)' : 'none',
                              opacity: 1,
                              cursor: 'pointer',
                            },
                            '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator:hover': {
                              opacity: 0.7,
                            },
                          }}
                          inputProps={{
                            sx: {
                              color: colors.primaryText,
                              '&::-webkit-calendar-picker-indicator': {
                                filter: isDark ? 'invert(1)' : 'none',
                              },
                            }
                          }}
                        />

                        {/* 통합 계약 검색 */}
                        <Autocomplete
                          options={rowContractOptions}
                          getOptionLabel={(option) => {
                            if (option.divider) return '';
                            if (option.display_label) return option.display_label;
                            const companyName = option.company_name_kr || '';
                            const contractTitle = option.contract_title || '';
                            return contractTitle && companyName
                              ? `${companyName} - ${contractTitle}`.trim()
                              : companyName || contractTitle || '';
                          }}
                          isOptionEqualToValue={(option, value) => {
                            if (!option || !value) return false;
                            // contract_id가 있으면 contract_id로 비교, 없으면 company_code로 비교
                            if (option.contract_id && value.contract_id) {
                              return option.contract_id === value.contract_id && option.company_code === value.company_code;
                            }
                            return option.company_code === value.company_code;
                          }}
                          value={row.contract}
                          onChange={(_, newValue) => {
                            if (newValue && !newValue.divider) {
                              handleRowContractSelect(row.id, newValue);
                            }
                          }}
                          onInputChange={(_, newInputValue) => {
                            if (newInputValue.length >= 2) {
                              searchContracts(newInputValue);
                            }
                          }}
                          loading={isSearchingContracts}
                          size="small"
                          sx={{ minWidth: 280, maxWidth: 350, flex: '1 1 280px' }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="업체/계약 검색"
                              placeholder="업체명 또는 계약명을 입력하세요"
                              inputProps={{
                                ...params.inputProps,
                                'data-row-id': row.id,
                                'data-field': 'contract',
                              }}
                              onKeyDown={(e) => {
                                // Tab: 다음 필드(업무 구분)로 이동
                                if (e.key === 'Tab' && !e.shiftKey) {
                                  e.preventDefault();
                                  setTimeout(() => {
                                    const nextField = document.querySelector(`input[data-row-id="${row.id}"][data-field="job_category"]`);
                                    if (nextField) {
                                      nextField.focus();
                                      nextField.select();
                                    }
                                  }, 10);
                                }
                                // Shift+Tab: 이전 행의 마지막 필드(투입시간)로 이동
                                else if (e.key === 'Tab' && e.shiftKey) {
                                  e.preventDefault();
                                  if (index > 0) {
                                    setTimeout(() => {
                                      const prevRowField = document.querySelector(`input[data-row-id="${inputRows[index - 1].id}"][data-field="input_minute"]`);
                                      if (prevRowField) {
                                        prevRowField.focus();
                                        prevRowField.select();
                                      }
                                    }, 10);
                                  }
                                }
                              }}
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <>
                                    <Search size={18} style={{ marginRight: 8, color: colors.secondaryText }} />
                                    {params.InputProps.startAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          renderOption={(props, option) => {
                            if (option.divider) {
                              return <Divider key="divider" />;
                            }
                            return (
                              <ListItem {...props} key={option.contract_id}>
                                {option.isRecent && (
                                  <Chip
                                    label="최근 사용"
                                    size="small"
                                    sx={{ mr: 1, height: 20, fontSize: '0.7rem' }}
                                    color="primary"
                                  />
                                )}
                                <ListItemText
                                  primary={option.display_label || (() => {
                                    const companyName = option.company_name_kr || '';
                                    const contractTitle = option.contract_title || '';
                                    return contractTitle && companyName
                                      ? `${companyName} - ${contractTitle}`.trim()
                                      : companyName || contractTitle || '';
                                  })()}
                                  secondary={option.contract_id}
                                />
                              </ListItem>
                            );
                          }}
                          noOptionsText="2자 이상 입력하세요"
                        />

                        {/* 업무 구분 */}
                        <TextField
                          label="업무 구분"
                          value={row.job_category || ''}
                          onChange={(e) => handleRowChange(row.id, 'job_category', e.target.value)}
                          placeholder="예: 정산, 정정, 보고서"
                          size="small"
                          inputProps={{
                            'data-row-id': row.id,
                            'data-field': 'job_category',
                          }}
                          onKeyDown={(e) => {
                            // Ctrl+Enter 또는 Cmd+Enter로 일괄 저장
                            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                              e.preventDefault();
                              e.stopPropagation();
                              handleBatchSubmit();
                            }
                            // Tab: 다음 필드(업무내용 상세)로 이동
                            else if (e.key === 'Tab' && !e.shiftKey) {
                              e.preventDefault();
                              setTimeout(() => {
                                const nextField = document.querySelector(`input[data-row-id="${row.id}"][data-field="input_description"]`);
                                if (nextField) {
                                  nextField.focus();
                                  nextField.select();
                                }
                              }, 10);
                            }
                            // Shift+Tab: 이전 필드(업체/계약 검색)로 이동
                            else if (e.key === 'Tab' && e.shiftKey) {
                              e.preventDefault();
                              setTimeout(() => {
                                const prevField = document.querySelector(`input[data-row-id="${row.id}"][data-field="contract"]`);
                                if (prevField) {
                                  prevField.focus();
                                }
                              }, 10);
                            }
                          }}
                          sx={{ minWidth: 150, flex: '1 1 150px' }}
                        />

                        {/* 업무내용 상세 */}
                        <TextField
                          label="업무내용 상세"
                          value={row.input_description}
                          onChange={(e) => handleRowChange(row.id, 'input_description', e.target.value)}
                          size="small"
                          placeholder="예: AEO 인증 컨설팅 (Ctrl+Enter로 저장)"
                          inputProps={{
                            'data-row-id': row.id,
                            'data-field': 'input_description',
                          }}
                          onKeyDown={(e) => {
                            // Ctrl+Enter 또는 Cmd+Enter로 일괄 저장
                            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                              e.preventDefault();
                              e.stopPropagation();
                              handleBatchSubmit();
                            }
                            // Tab: 다음 필드(투입시간)로 이동
                            else if (e.key === 'Tab' && !e.shiftKey) {
                              e.preventDefault();
                              setTimeout(() => {
                                const nextField = document.querySelector(`input[data-row-id="${row.id}"][data-field="input_minute"]`);
                                if (nextField) {
                                  nextField.focus();
                                  nextField.select();
                                }
                              }, 10);
                            }
                            // Shift+Tab: 이전 필드(업무 구분)로 이동
                            else if (e.key === 'Tab' && e.shiftKey) {
                              e.preventDefault();
                              setTimeout(() => {
                                const prevField = document.querySelector(`input[data-row-id="${row.id}"][data-field="job_category"]`);
                                if (prevField) {
                                  prevField.focus();
                                }
                              }, 10);
                            }
                          }}
                          sx={{ minWidth: 200, flex: '3 1 300px' }}
                        />

                        {/* 투입시간 */}
                        <TextField
                          type="number"
                          label="투입시간"
                          value={row.input_minute}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || value === null || value === undefined) {
                              handleRowChange(row.id, 'input_minute', '');
                              return;
                            }
                            const numValue = parseInt(value, 10);
                            if (!isNaN(numValue) && numValue > 0) {
                              handleRowChange(row.id, 'input_minute', numValue.toString());
                            } else if (value === '') {
                              handleRowChange(row.id, 'input_minute', '');
                            }
                          }}
                          onBlur={(e) => {
                            const value = parseInt(e.target.value, 10);
                            if (isNaN(value) || value <= 0) {
                              handleRowChange(row.id, 'input_minute', '1');
                            }
                          }}
                          onKeyDown={(e) => {
                            // Ctrl+Enter 또는 Cmd+Enter로 일괄 저장
                            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                              e.preventDefault();
                              e.stopPropagation();
                              handleBatchSubmit();
                            }
                            // Shift+Tab: 이전 필드(업무내용 상세)로 이동
                            else if (e.key === 'Tab' && e.shiftKey) {
                              e.preventDefault();
                              setTimeout(() => {
                                const prevField = document.querySelector(`input[data-row-id="${row.id}"][data-field="input_description"]`);
                                if (prevField) {
                                  prevField.focus();
                                }
                              }, 10);
                            }
                            // Tab: 마지막 행이면 행 추가, 아니면 다음 행의 첫 번째 필드로 이동
                            else if (e.key === 'Tab' && !e.shiftKey) {
                              e.preventDefault();
                              if (index === inputRows.length - 1) {
                                // 마지막 행에서 Tab 키를 누르면 행 추가
                                const newRowId = handleAddRow();
                                // 새로 추가된 행의 업체/계약 검색으로 포커스 이동
                                setTimeout(() => {
                                  const inputElement = document.querySelector(`input[data-row-id="${newRowId}"][data-field="contract"]`);
                                  if (inputElement) {
                                    inputElement.focus();
                                  }
                                }, 100);
                              } else {
                                // 다음 행의 첫 번째 필드(업체/계약 검색)로 이동
                                setTimeout(() => {
                                  const nextRowField = document.querySelector(`input[data-row-id="${inputRows[index + 1].id}"][data-field="contract"]`);
                                  if (nextRowField) {
                                    nextRowField.focus();
                                  }
                                }, 10);
                              }
                            }
                          }}
                          size="small"
                          inputProps={{
                            min: 1,
                            step: 1,
                            'data-row-id': row.id,
                            'data-field': 'input_minute',
                          }}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">분</InputAdornment>,
                          }}
                          sx={{ minWidth: 80, flexShrink: 0, maxWidth: 120 }}
                          required
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* 모바일 버전: 투입시간 입력 버튼 */}
      {isMobileView && (
        <Card sx={{ mb: 2, backgroundColor: isDark ? '#1C1D22' : '#F8F9FA' }}>
          <CardContent>
            {/* 입력 스트릭 표시 및 출근 시간 캐치업 경고 - 한 줄에 나란히 배치 */}
            {(streakData.current_streak > 0 || showMissingAlert) && (
              <Box sx={{ 
                mb: 2, 
                p: 1.5, 
                backgroundColor: isDark ? '#2A2B30' : '#FFF3E0', 
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap'
              }}>
                {streakData.current_streak > 0 && (
                  <Typography variant="body2" sx={{ fontWeight: 600, color: colors.accent }}>
                    🔥 {streakData.current_streak}일 연속 입력 달성!
                  </Typography>
                )}
                {showMissingAlert && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: colors.warning || '#FF9800' }}>
                      ⚠️ 어제({format(new Date(Date.now() - 86400000), 'yyyy-MM-dd')})의 로그가 비어있습니다. 투입시간 입력 버튼을 눌러 어제 날짜를 선택하여 입력해주세요.
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => setShowMissingAlert(false)}
                      sx={{ ml: 'auto' }}
                    >
                      <X size={16} />
                    </IconButton>
                  </Box>
                )}
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
              <Clock size={18} />
              <Typography variant="h6" sx={{ color: colors.primaryText, fontWeight: 600 }}>
                투입시간 입력
              </Typography>
            </Box>

            <Button
              variant="contained"
              fullWidth
              startIcon={<Plus size={20} />}
              onClick={() => {
                setBottomSheetData({
                  log_date: format(new Date(), 'yyyy-MM-dd'),
                  contract: null,
                  work_description: '',
                  input_minute: '',
                  input_description: '',
                });
                setBottomSheetOpen(true);
              }}
              sx={{
                backgroundColor: colors.accent,
                minHeight: '56px',
                fontSize: '14px',
                fontWeight: 600,
                borderRadius: 2,
              }}
            >
              투입시간 입력하기
            </Button>
          </CardContent>
        </Card>
      )}

      {/* 투입시간 목록 테이블 (PC 버전) */}
      {!isMobileView && (
      <Card sx={{ display: 'flex', flexDirection: 'column', borderRadius: 0 }}>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Grid size={18} />
              <Typography variant="h6" sx={{ color: colors.primaryText, fontWeight: 600 }}>
                투입시간 목록
              </Typography>
              {/* 조회 기간 설정 */}
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', ml: 3 }}>
                <TextField
                  type="date"
                  label="시작일"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" sx={{ mr: -0.5 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleRangeDateStep('from', 1)}
                            sx={{ p: 0.25, color: colors.secondaryText }}
                            aria-label="시작일 하루 증가"
                          >
                            <ChevronUp size={14} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleRangeDateStep('from', -1)}
                            sx={{ p: 0.25, color: colors.secondaryText }}
                            aria-label="시작일 하루 감소"
                          >
                            <ChevronDown size={14} />
                          </IconButton>
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ 
                    width: 160,
                    '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                      filter: isDark ? 'invert(1)' : 'none',
                      opacity: 1,
                      cursor: 'pointer',
                    },
                    '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator:hover': {
                      opacity: 0.7,
                    },
                  }}
                  inputProps={{
                    sx: {
                      color: colors.primaryText,
                      '&::-webkit-calendar-picker-indicator': {
                        filter: isDark ? 'invert(1)' : 'none',
                      },
                    }
                  }}
                />
                <Typography variant="body2" sx={{ color: colors.secondaryText }}>
                  ~
                </Typography>
                <TextField
                  type="date"
                  label="종료일"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" sx={{ mr: -0.5 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleRangeDateStep('to', 1)}
                            sx={{ p: 0.25, color: colors.secondaryText }}
                            aria-label="종료일 하루 증가"
                          >
                            <ChevronUp size={14} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleRangeDateStep('to', -1)}
                            sx={{ p: 0.25, color: colors.secondaryText }}
                            aria-label="종료일 하루 감소"
                          >
                            <ChevronDown size={14} />
                          </IconButton>
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ 
                    width: 160,
                    '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                      filter: isDark ? 'invert(1)' : 'none',
                      opacity: 1,
                      cursor: 'pointer',
                    },
                    '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator:hover': {
                      opacity: 0.7,
                    },
                  }}
                  inputProps={{
                    sx: {
                      color: colors.primaryText,
                      '&::-webkit-calendar-picker-indicator': {
                        filter: isDark ? 'invert(1)' : 'none',
                      },
                    }
                  }}
                />
                {/* 새로고침 버튼 */}
                <Tooltip title="목록 새로고침">
                  <IconButton
                    size="small"
                    onClick={async () => {
                      console.log('[새로고침] 현재 날짜 범위:', { fromDate, toDate });
                      await fetchTimelogs();
                    }}
                    sx={{
                      color: colors.accent,
                      '&:hover': {
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                      },
                    }}
                  >
                    <RefreshCw size={18} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            {/* 우측 상단: 통계 정보 및 엑셀 다운로드 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* 통계 정보 */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" sx={{ color: colors.secondaryText }}>
                  총 <strong style={{ color: colors.primaryText }}>{summaryStats.totalCount}</strong>건
                </Typography>
                <Typography variant="body2" sx={{ color: colors.secondaryText }}>
                  총 투입시간: <strong style={{ color: colors.accent }}>{formatMinutesToHoursAndMinutes(summaryStats.totalMinutes)}</strong>
                </Typography>
              </Box>
              {/* 엑셀 다운로드 버튼 */}
              <Tooltip title="엑셀 다운로드">
                <span>
                  <IconButton
                    size="small"
                    onClick={handleDownloadExcel}
                    disabled={timelogs.length === 0}
                    sx={{
                      color: timelogs.length === 0 ? colors.secondaryText : colors.accent,
                      '&:hover': {
                        backgroundColor: timelogs.length === 0 ? 'transparent' : (isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'),
                      },
                      '&.Mui-disabled': {
                        color: colors.secondaryText,
                        opacity: 0.5,
                      },
                    }}
                  >
                    <Download size={18} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
          <TableContainer 
            data-table-container="timelog"
            sx={{ 
              overflow: 'auto',
              maxHeight: 'calc(100vh - 420px)',  // 상단 요소들을 제외한 최대 높이
              position: 'relative',
              width: '100%',
            }}
          >
            <Table 
              stickyHeader 
              size="small" 
              sx={{ 
                width: '100%', 
                borderCollapse: 'separate', 
                borderSpacing: 0, 
                fontSize: '14px', 
                '& .MuiTableCell-root': { fontSize: '14px' }, 
                tableLayout: 'fixed' // fixed 레이아웃으로 변경하여 동적 너비 조정
              }}
            >
              <TableHead sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: isDark ? '#1C1D22' : '#E8EAED' }}>
                <TableRow>
                  <TableCell
                    onClick={() => handleSort('timelog_id')}
                    sx={{
                      width: getColumnWidth('timelog_id'),
                      minWidth: '80px',
                      backgroundColor: isDark ? '#1C1D22' : '#E8EAED',
                      fontWeight: 600,
                      border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
                      fontSize: '14px',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': {
                        backgroundColor: isDark ? '#2A2B30' : '#D0D3D6',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      ID
                      {sortConfig.column === 'timelog_id' && (
                        sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('log_date')}
                    sx={{
                      width: getColumnWidth('log_date'),
                      minWidth: '110px',  // 업무일자 - 최소 너비 확대 (90px → 110px)
                      backgroundColor: isDark ? '#1C1D22' : '#E8EAED',
                      fontWeight: 600,
                      border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
                      fontSize: '14px',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': {
                        backgroundColor: isDark ? '#2A2B30' : '#D0D3D6',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      업무일자
                      {sortConfig.column === 'log_date' && (
                        sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      )}
                    </Box>
                  </TableCell>
                  {/* <TableCell
                    onClick={() => handleSort('company_code')}
                    sx={{
                      width: getColumnWidth('company_code'),
                      minWidth: '100px',
                      backgroundColor: isDark ? '#1C1D22' : '#E8EAED',
                      fontWeight: 600,
                      border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
                      fontSize: '14px',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': {
                        backgroundColor: isDark ? '#2A2B30' : '#D0D3D6',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      업체코드
                      {sortConfig.column === 'company_code' && (
                        sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      )}
                    </Box>
                  </TableCell> */}
                  <TableCell
                    onClick={() => handleSort('company_name_kr')}
                    sx={{
                      width: getColumnWidth('company_name'),
                      minWidth: '120px',
                      backgroundColor: isDark ? '#1C1D22' : '#E8EAED',
                      fontWeight: 600,
                      border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
                      fontSize: '14px',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': {
                        backgroundColor: isDark ? '#2A2B30' : '#D0D3D6',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      업체명
                      {sortConfig.column === 'company_name_kr' && (
                        sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('contract_id')}
                    sx={{
                      width: getColumnWidth('contract_id'),
                      minWidth: '110px',
                      backgroundColor: isDark ? '#1C1D22' : '#E8EAED',
                      fontWeight: 600,
                      border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
                      fontSize: '14px',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': {
                        backgroundColor: isDark ? '#2A2B30' : '#D0D3D6',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      계약번호
                      {sortConfig.column === 'contract_id' && (
                        sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('contract_title')}
                    sx={{
                      width: getColumnWidth('contract_title'),
                      minWidth: '120px',
                      backgroundColor: isDark ? '#1C1D22' : '#E8EAED',
                      fontWeight: 600,
                      border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
                      fontSize: '14px',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': {
                        backgroundColor: isDark ? '#2A2B30' : '#D0D3D6',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      계약 명칭
                      {sortConfig.column === 'contract_title' && (
                        sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('job_category')}
                    sx={{
                      width: getColumnWidth('job_category'),
                      minWidth: '100px',
                      backgroundColor: isDark ? '#1C1D22' : '#E8EAED',
                      fontWeight: 600,
                      border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
                      fontSize: '14px',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': {
                        backgroundColor: isDark ? '#2A2B30' : '#D0D3D6',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      업무 구분
                      {sortConfig.column === 'job_category' && (
                        sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('work_description')}
                    sx={{
                      width: getColumnWidth('work_description'),
                      minWidth: '220px',  // 업무내용 상세 - 최소 너비 확대 (180px → 220px)
                      backgroundColor: isDark ? '#1C1D22' : '#E8EAED',
                      fontWeight: 600,
                      border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
                      fontSize: '14px',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': {
                        backgroundColor: isDark ? '#2A2B30' : '#D0D3D6',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      업무내용 상세
                      {sortConfig.column === 'work_description' && (
                        sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('input_minute')}
                    sx={{
                      width: getColumnWidth('input_minute'),
                      minWidth: '100px',  // 투입시간(분) - 헤더 텍스트가 잘리지 않도록 최소 너비 조정 (80px → 100px)
                      backgroundColor: isDark ? '#1C1D22' : '#E8EAED',
                      fontWeight: 600,
                      border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
                      fontSize: '14px',
                      cursor: 'pointer',
                      userSelect: 'none',
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        backgroundColor: isDark ? '#2A2B30' : '#D0D3D6',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      투입시간(분)
                      {sortConfig.column === 'input_minute' && (
                        sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('created_at')}
                    sx={{
                      width: getColumnWidth('created_at'),
                      minWidth: '150px',
                      backgroundColor: isDark ? '#1C1D22' : '#E8EAED',
                      fontWeight: 600,
                      border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
                      fontSize: '14px',
                      cursor: 'pointer',
                      userSelect: 'none',
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        backgroundColor: isDark ? '#2A2B30' : '#D0D3D6',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      생성일시
                      {sortConfig.column === 'created_at' && (
                        sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      width: getColumnWidth('notes'),
                      minWidth: '120px',
                      backgroundColor: isDark ? '#1C1D22' : '#E8EAED',
                      fontWeight: 600,
                      border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
                      fontSize: '14px',
                    }}
                  >
                    비고
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedTimelogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4, color: colors.secondaryText, fontSize: '14px' }}>
                      등록된 투입시간이 없습니다. 업무 투입시간 입력을 사용하여 추가하세요.
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedTimelogs.map((row) => {
                    const isEditing = editingRowId === row.timelog_id;
                    return (
                      <ResizableTableRow
                        key={row.timelog_id}
                        rowId={row.timelog_id}
                        height={rowHeights[row.timelog_id] || 48}
                        onHeightChange={handleRowHeightChange}
                        onDoubleClick={() => handleRowDoubleClick(row.timelog_id)}
                        tabIndex={0}
                        data-row-id={row.timelog_id}
                        onKeyDown={(e) => {
                          if (e.key === 'F2') {
                            e.preventDefault();
                            if (isEditing) {
                              // 이미 편집 모드인 경우: 첫 번째 셀에 커서 활성화
                              handleCellClick(row.timelog_id, 'log_date', row.log_date);
                            } else {
                              // 편집 모드가 아닌 경우: 편집 모드로 전환
                              setEditingRowId(row.timelog_id);
                            }
                          }
                        }}
                        sx={{
                          '&:hover': {
                            backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                          },
                          '&:focus': {
                            outline: `2px solid ${colors.accent}`,
                            outlineOffset: '-2px',
                            backgroundColor: isDark ? 'rgba(138,124,255,0.05)' : 'rgba(138,124,255,0.05)',
                          },
                          cursor: isEditing ? 'default' : 'pointer',
                        }}
                      >
                        <TableCell sx={{ 
                          border: `1px dashed ${isDark ? '#555' : '#ccc'}`, 
                          fontSize: '14px',
                          width: getColumnWidth('timelog_id'),
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'default',
                        }}>
                          {formatTimelogId(row.timelog_id)}
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
                            cursor: isEditing ? 'pointer' : 'default',
                            fontSize: '14px',
                            width: getColumnWidth('log_date'),
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            // 편집 가능한 필드에 음영 효과 추가
                            backgroundColor: isEditing 
                              ? (isDark ? 'rgba(138,124,255,0.1)' : 'rgba(138,124,255,0.05)')
                              : 'transparent',
                            transition: 'all 0.2s ease',
                            '&:hover': isEditing ? {
                              backgroundColor: isDark ? 'rgba(138,124,255,0.15)' : 'rgba(138,124,255,0.1)',
                            } : {},
                          }}
                          onClick={() => isEditing && handleCellClick(row.timelog_id, 'log_date', row.log_date)}
                        >
                          {isEditing ? renderEditableCell(row, 'log_date', row.log_date, 'date') : (row.log_date || '-')}
                        </TableCell>
                        {/* <TableCell 
                          sx={{ 
                            border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
                            cursor: isEditing ? 'pointer' : 'default',
                            fontSize: '14px',
                            width: getColumnWidth('company_code'),
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          onClick={() => isEditing && handleCellClick(row.timelog_id, 'company_code', row.company_code)}
                        >
                          {isEditing ? renderEditableCell(row, 'company_code', row.company_code) : (row.company_name_kr || row.company_name || row.company_code || '-')}
                        </TableCell> */}
                        <TableCell sx={{ 
                          border: `1px dashed ${isDark ? '#555' : '#ccc'}`, 
                          fontSize: '14px',
                          width: getColumnWidth('company_name'),
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'default',
                        }}>
                          {row.company_name_kr || row.company_name || '-'}
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
                            fontSize: '14px',
                            width: getColumnWidth('contract_id'),
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            cursor: 'default',
                          }}
                        >
                          {row.contract_id || '-'}
                        </TableCell>
                        <TableCell sx={{ 
                          border: `1px dashed ${isDark ? '#555' : '#ccc'}`, 
                          fontSize: '14px',
                          width: getColumnWidth('contract_title'),
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'default',
                        }}>
                          {row.contract_title || '-'}
                        </TableCell>
                        <TableCell sx={{ 
                          border: `1px dashed ${isDark ? '#555' : '#ccc'}`, 
                          fontSize: '14px',
                          width: getColumnWidth('job_category'),
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'default',
                        }}>
                          {row.job_category || '-'}
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
                            cursor: isEditing ? 'pointer' : 'default',
                            fontSize: '14px',
                            width: getColumnWidth('work_description'),
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            // 편집 가능한 필드에 음영 효과 추가
                            backgroundColor: isEditing 
                              ? (isDark ? 'rgba(138,124,255,0.1)' : 'rgba(138,124,255,0.05)')
                              : 'transparent',
                            transition: 'all 0.2s ease',
                            '&:hover': isEditing ? {
                              backgroundColor: isDark ? 'rgba(138,124,255,0.15)' : 'rgba(138,124,255,0.1)',
                            } : {},
                          }}
                          onClick={() => isEditing && handleCellClick(row.timelog_id, 'work_description', row.work_description)}
                        >
                          {isEditing ? renderEditableCell(row, 'work_description', row.work_description) : (row.work_description || '-')}
                        </TableCell>
                        <TableCell 
                          align="right" 
                          sx={{ 
                            border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
                            cursor: isEditing ? 'pointer' : 'default',
                            fontSize: '14px',
                            width: getColumnWidth('input_minute'),
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            // 편집 가능한 필드에 음영 효과 추가
                            backgroundColor: isEditing 
                              ? (isDark ? 'rgba(138,124,255,0.1)' : 'rgba(138,124,255,0.05)')
                              : 'transparent',
                            transition: 'all 0.2s ease',
                            '&:hover': isEditing ? {
                              backgroundColor: isDark ? 'rgba(138,124,255,0.15)' : 'rgba(138,124,255,0.1)',
                            } : {},
                          }}
                          onClick={() => isEditing && handleCellClick(row.timelog_id, 'input_minute', row.input_minute)}
                        >
                          {isEditing ? renderEditableCell(row, 'input_minute', row.input_minute, 'number') : (row.input_minute || 0)}
                        </TableCell>
                        <TableCell sx={{ 
                          border: `1px dashed ${isDark ? '#555' : '#ccc'}`, 
                          fontSize: '14px',
                          width: getColumnWidth('created_at'),
                          minWidth: '150px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'default',
                        }}>
                          {row.created_at ? format(new Date(row.created_at), 'yyyy-MM-dd HH:mm') : '-'}
                        </TableCell>
                        <TableCell align="center" sx={{ 
                          border: `1px dashed ${isDark ? '#555' : '#ccc'}`, 
                          fontSize: '14px',
                          width: getColumnWidth('notes'),
                          minWidth: '120px',
                          cursor: 'default',
                        }}>
                          {isEditing ? (
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              <Tooltip title="저장">
                                <IconButton
                                  size="small"
                                  onMouseDown={(e) => {
                                    e.preventDefault(); // blur 이벤트 방지
                                  }}
                                  onClick={() => handleSaveClick(row.timelog_id)}
                                  sx={{ color: colors.accent }}
                                >
                                  <Save size={16} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="취소">
                                <IconButton
                                  size="small"
                                  onClick={handleCancelEdit}
                                  sx={{ color: colors.secondaryText }}
                                >
                                  <X size={16} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              <Tooltip title="수정">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditClick(row.timelog_id)}
                                  sx={{ color: colors.accent }}
                                >
                                  <Edit2 size={16} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="삭제">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(row.timelog_id)}
                                  sx={{ color: colors.error }}
                                >
                                  <Trash2 size={16} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </TableCell>
                      </ResizableTableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
      )}

      {/* 모바일 투입시간 목록 */}
      {isMobileView && (
        <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 0, mt: 2 }}>
          <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Grid size={18} />
                <Typography variant="h6" sx={{ color: colors.primaryText, fontWeight: 600 }}>
                  투입시간 목록
                </Typography>
              </Box>
              {/* 정렬 옵션 */}
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={mobileSortOption}
                  onChange={(e) => setMobileSortOption(e.target.value)}
                  sx={{
                    fontSize: '12px',
                    height: '32px',
                    '& .MuiSelect-select': {
                      py: 0.5,
                    },
                  }}
                >
                  <MenuItem value="date">날짜순</MenuItem>
                  <MenuItem value="time">투입시간</MenuItem>
                  <MenuItem value="company">업체명</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            {/* 조회 기간 설정 */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                type="date"
                label="시작일"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ mr: -0.5 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleRangeDateStep('from', 1)}
                          sx={{ p: 0.25, color: colors.secondaryText }}
                          aria-label="시작일 하루 증가"
                        >
                          <ChevronUp size={14} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleRangeDateStep('from', -1)}
                          sx={{ p: 0.25, color: colors.secondaryText }}
                          aria-label="시작일 하루 감소"
                        >
                          <ChevronDown size={14} />
                        </IconButton>
                      </Box>
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  flex: 1, 
                  fontSize: '12px',
                  '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                    filter: isDark ? 'invert(1)' : 'none',
                    opacity: 1,
                    cursor: 'pointer',
                  },
                  '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator:hover': {
                    opacity: 0.7,
                  },
                }}
                inputProps={{
                  sx: {
                    color: colors.primaryText,
                    '&::-webkit-calendar-picker-indicator': {
                      filter: isDark ? 'invert(1)' : 'none',
                    },
                  }
                }}
              />
              <Typography variant="body2" sx={{ alignSelf: 'center', color: colors.secondaryText }}>
                ~
              </Typography>
              <TextField
                type="date"
                label="종료일"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ mr: -0.5 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleRangeDateStep('to', 1)}
                          sx={{ p: 0.25, color: colors.secondaryText }}
                          aria-label="종료일 하루 증가"
                        >
                          <ChevronUp size={14} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleRangeDateStep('to', -1)}
                          sx={{ p: 0.25, color: colors.secondaryText }}
                          aria-label="종료일 하루 감소"
                        >
                          <ChevronDown size={14} />
                        </IconButton>
                      </Box>
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  flex: 1, 
                  fontSize: '12px',
                  '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                    filter: isDark ? 'invert(1)' : 'none',
                    opacity: 1,
                    cursor: 'pointer',
                  },
                  '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator:hover': {
                    opacity: 0.7,
                  },
                }}
                inputProps={{
                  sx: {
                    color: colors.primaryText,
                    '&::-webkit-calendar-picker-indicator': {
                      filter: isDark ? 'invert(1)' : 'none',
                    },
                  }
                }}
              />
              <IconButton
                size="small"
                onClick={async () => {
                  await fetchTimelogs();
                }}
                sx={{
                  color: colors.accent,
                }}
              >
                <RefreshCw size={18} />
              </IconButton>
            </Box>

            {/* 모바일 목록 */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {sortedTimelogs.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" sx={{ color: colors.secondaryText }}>
                    등록된 투입시간이 없습니다.
                  </Typography>
                </Box>
              ) : (
                <List sx={{ p: 0 }}>
                  {sortedTimelogs.map((log) => {
                    const logDate = log.log_date ? format(new Date(log.log_date), 'yyyy.MM.dd') : '-';
                    const companyName = log.company_name_kr || '-';
                    const contractTitle = log.contract_title || '-';
                    const inputMinute = log.input_minute || 0;
                    const workDescription = log.work_description || '-';
                    
                    return (
                      <Card
                        key={log.timelog_id}
                        sx={{
                          mb: 1.5,
                          backgroundColor: isDark ? '#2A2B30' : '#F8F9FA',
                          border: `1px solid ${colors.primaryBorder}`,
                          borderRadius: 2,
                          '&:hover': {
                            backgroundColor: isDark ? '#333438' : '#F0F0F0',
                          },
                        }}
                      >
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          {/* 첫 줄: 날짜, 업체명, 계약 명칭 */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: colors.primaryText, fontSize: '13px' }}>
                              ({logDate}) {companyName}
                            </Typography>
                            <Typography variant="body2" sx={{ color: colors.secondaryText, fontSize: '12px' }}>
                              {contractTitle}
                            </Typography>
                          </Box>
                          
                          {/* 둘째 줄: 투입시간, 업무내용 */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: colors.accent, fontSize: '13px' }}>
                              투입시간 {inputMinute}분
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: colors.secondaryText, 
                                fontSize: '12px',
                                flex: 1,
                                textAlign: 'right',
                                ml: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {workDescription}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })}
                </List>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* 모바일 To-Do Drawer */}
      {isMobileView && (
        <MobileTodoDrawer
          open={todoDrawerOpen}
          onOpen={() => setTodoDrawerOpen(true)}
          onClose={() => setTodoDrawerOpen(false)}
          onTodoDragStart={(todo) => {
            setDraggingTodo(todo);
          }}
          onTodoClick={async (todo) => {
            // To-Do 클릭 시 Bottom Sheet 열고 자동 채우기
            const contract = [...contractOptions, ...recentContracts].find(
              c => c.contract_id === todo.contract_id
            );
            
            setBottomSheetData({
              log_date: format(new Date(), 'yyyy-MM-dd'),
              contract: contract || null,
              work_description: todo.work_description || todo.note_1 || '',
              input_minute: 60,
              input_description: todo.note_1 || '',
            });
            
            if (contract && contract.contract_id) {
              await fetchSuggestions(contract.contract_id);
            }
            
            setBottomSheetOpen(true);
            setTodoDrawerOpen(false);
          }}
        />
      )}

      {/* 모바일 Bottom Sheet */}
      {isMobileView && (
        <MobileBottomSheet
          open={bottomSheetOpen}
          onClose={() => {
            setBottomSheetOpen(false);
            setBottomSheetData(null);
          }}
          onSubmit={async (data) => {
            try {
              // contract_id 타입 변환 (이미 선택된 계약이므로 타입만 확인)
              if (!data.contract_id && data.contract_id !== 0) {
                alert('계약을 선택해주세요.');
                return;
              }
              
              // contract_id는 숫자 또는 문자열 모두 허용 (예: 123 또는 "THE-C-2024-086")
              let finalContractId;
              if (typeof data.contract_id === 'number') {
                finalContractId = data.contract_id;
              } else if (typeof data.contract_id === 'string' && data.contract_id.trim() !== '') {
                // 문자열 형식의 contract_id도 허용 (예: "THE-C-2024-086")
                finalContractId = data.contract_id.trim();
              } else {
                console.error('❌ [모바일 Bottom Sheet] contract_id 타입 오류:', typeof data.contract_id, data.contract_id);
                alert('계약 ID가 유효하지 않습니다.');
                return;
              }
              
              const processedData = {
                ...data,
                contract_id: finalContractId
              };

              // Zod 검증 적용 (사용자 입력 필드에만 집중)
              const validation = validateData(TimelogCreateSchema, processedData);
              
              if (!validation.success) {
                alert(validation.error);
                return;
              }
              
              // 검증된 데이터로 API 호출
              await axios.post(`${API_BASE_URL}/timelogs`, validation.data);
              fetchTimelogs();
              fetchStreak();
              setBottomSheetOpen(false);
              setBottomSheetData(null);
              // 오늘 업무시간 업데이트 이벤트 발생
              window.dispatchEvent(new Event('timelogUpdated'));
            } catch (error) {
              alert(error.response?.data?.detail || '저장 실패');
            }
          }}
          initialData={bottomSheetData}
          contracts={[...contractOptions, ...recentContracts].filter((opt, idx, arr) =>
            arr.findIndex(o => o.contract_id === opt.contract_id) === idx
          )}
          suggestions={suggestions}
          onSearchContracts={async (query) => {
            if (typeof query === 'string' && query.length >= 2) {
              await searchContracts(query);
            } else if (typeof query === 'string') {
              // contract_id로 검색
              const contract = [...contractOptions, ...recentContracts].find(c => c.contract_id === query);
              if (contract && contract.contract_id) {
                await fetchSuggestions(contract.contract_id);
              }
            }
          }}
          title={bottomSheetData ? '투입시간 수정' : '투입시간 입력'}
        />
      )}

      {/* 삭제 확인 모달 */}
      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        title="행 삭제 확인"
        message={deleteConfirmMessage}
        confirmText="삭제"
        cancelText="취소"
        confirmColor={colors.error || '#f44336'}
        theme={isDark ? 'dark' : 'light'}
      />

      {/* 정보 모달 (저장 성공/알림) */}
      <InfoModal
        isOpen={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        title={infoModalTitle}
        message={infoModalMessage}
        type="success"
      />
    </Box>
  );
};

export default JobTimeInput;
